import {
  CrudFilters,
  HttpError,
  useOne,
  useTable,
  useTranslation,
  useUpdate,
} from "@refinedev/core";
import { EyeIcon, PencilIcon, SearchIcon } from "@heroicons/react/outline";
import { get } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Button from "components/button";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { Label } from "components/label";
import Pagination from "components/pagination";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { TooltipParagraph } from "components/tooltipParagraph";
import { isHaveActionPermission } from "utills/helpers";
import { getConfig, PAGE_SIZE } from "../config";
import { getEventLabels } from "../labels";
import { PickupEventConfig, PickupEventItem, PickupEventVariant } from "../types";
import { getMessage } from "../utils";

const SharedFilter = ({
  config,
  currentFilterValues,
  setFilters,
  variant,
}: {
  config: PickupEventConfig;
  currentFilterValues: any;
  setFilters: (filters: CrudFilters) => void;
  variant: PickupEventVariant;
}) => {
  const { translate } = useTranslation();
  const labels = getEventLabels(variant, translate);
  const [eventId, setEventId] = useState(currentFilterValues.event_id);

  const { data, isLoading } = useOne<{ id: string; name: string }[], HttpError>({
    resource: config.dropdownResource,
    id: "",
    meta: {
      resource: "data",
    },
  });

  const eventOptions = (data?.data ?? []).map((event) => ({
    value: event.id,
    label: event.name,
  }));

  const submitFilter = () => {
    setFilters([
      { field: "event_id", operator: "eq", value: eventId },
      { field: "page", operator: "eq", value: 1 },
    ]);
  };

  const wrapperClass =
    variant === "wps"
      ? "box-container w-full flex flex-col gap-3 sm:flex-row sm:items-end"
      : "w-full flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3";

  return (
    <form
      className={wrapperClass}
      onSubmit={(event) => {
        event.preventDefault();
        submitFilter();
      }}
    >
      <div className={variant === "wps" ? "w-full md:w-[400px]" : "w-full md:max-w-[400px]"}>
        <Label id="event_id" name={labels.events} />
        <div className="mt-2">
          <Select
            options={eventOptions}
            isLoading={isLoading}
            isClearable={true}
            id="event_id"
            placeholder={labels.eventPlaceholder}
            className="py-0.5"
            value={eventOptions.find((item) => item.value === eventId)}
            onChange={(selected: { label: string; value: string }) =>
              setEventId(selected?.value ?? "")
            }
          />
        </div>
      </div>
      <div className={variant === "wps" ? "" : "shrink-0"}>
        <div className="w-full">
          <Button
            type="submit"
            className="w-full sm:w-[120px] bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
          >
            <SearchIcon className="w-4 h-4 text-brand-500 mr-2" /> {labels.search}
          </Button>
        </div>
      </div>
    </form>
  );
};

const formatDate = (value: string) => {
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm:ss") : "";
};

const EventActions = ({
  config,
  item,
}: {
  config: PickupEventConfig;
  item: PickupEventItem;
}) => (
  <div className="flex items-center gap-2">
    {isHaveActionPermission(config.permissionResource, config.permissionApp, "view") ? (
      <Link to={config.detailPath(item.id)} className="w-fit">
        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
          <EyeIcon className="w-4 h-4 text-black" />
        </Button>
      </Link>
    ) : null}
    {isHaveActionPermission(config.permissionResource, config.permissionApp, "edit") ? (
      <Link to={config.editPath(item.id)} className="w-fit">
        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
          <PencilIcon className="w-4 h-4 text-black" />
        </Button>
      </Link>
    ) : null}
  </div>
);

const MobileCards = ({
  config,
  currentPage,
  data,
  hasPagination,
  labels,
  pageSize,
  toggleActive,
  variant,
}: any) => (
  <div
    className={
      variant === "wps"
        ? `${hasPagination ? "mb-6" : ""} w-full flex flex-col gap-[8px]`
        : "bg-white"
    }
  >
    {data.map((item: PickupEventItem, index: number) => (
      <div
        key={item.id || index}
        className={
          variant === "wps"
            ? "w-full bg-white p-[16px] rounded-[8px] shadow-md"
            : "flex flex-col gap-y-4 overflow-hidden shadow-md ring-1 ring-gray-900/5 [&:not(:last-child)]:mb-4 p-4 rounded-xl"
        }
      >
        <div className="w-full flex items-center justify-between">
          {variant === "admin" ? (
            <SwitchButton
              checked={item.pickup}
              disabled={!isHaveActionPermission(config.permissionResource, config.permissionApp, "edit")}
              onChange={() => toggleActive(item.id, item.pickup)}
            />
          ) : (
            <div className="opacity-0">{labels.action}</div>
          )}
          <EventActions config={config} item={item} />
        </div>

        <div className={variant === "wps" ? "w-full mt-[16px] flex flex-col gap-[8px]" : "flex flex-col gap-y-4"}>
          {[
            [labels.no, String((currentPage - 1) * pageSize + 1 + index)],
            [labels.eventName, item.name],
            [labels.startDate, formatDate(item.start_date)],
          ].map(([label, value]) => (
            <div className={variant === "wps" ? "w-full flex" : "relative flex items-start justify-between gap-x-6"} key={label}>
              <span className={variant === "wps" ? "w-[30%] text-[14px] font-[500] text-[#262626]" : "flex-1 text-sm font-semibold leading-6"}>
                {label}
              </span>
              <span className={variant === "wps" ? "w-[70%] text-[14px] font-[400] text-[#2B3033] text-right" : "flex-1 text-right text-sm leading-6"}>
                {value}
              </span>
            </div>
          ))}
          {variant === "wps" ? (
            <div className="w-full flex">
              <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                {labels.display}
              </span>
              <div className="w-full flex justify-end">
                <SwitchButton
                  checked={item.pickup}
                  disabled={!isHaveActionPermission(config.permissionResource, config.permissionApp, "edit")}
                  onChange={() => toggleActive(item.id, item.pickup)}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    ))}
  </div>
);

const DesktopTable = ({
  config,
  currentPage,
  data,
  hasPagination,
  labels,
  pageSize,
  toggleActive,
  variant,
}: any) => (
  <div className="flow-root">
    <div className="relative overflow-x-auto min-w-full">
      <table
        className={`${hasPagination ? "mb-6" : ""} w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md`}
      >
        <thead>
          <tr>
            <th scope="col" className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">
              {labels.no}
            </th>
            <th scope="col" className="th">{labels.eventName}</th>
            <th scope="col" className="th">{labels.startDate}</th>
            <th scope="col" className="th">{labels.display}</th>
            <th scope="col" className="th w-28">
              <span className="sr-only">{labels.action}</span>
              <p>{labels.action}</p>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.map((item: PickupEventItem, index: number) => (
            <tr key={item.id || index}>
              <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                {(currentPage - 1) * pageSize + 1 + index}
              </td>
              <td className={variant === "wps" ? "td min-w-[230px] w-[480px]" : "td overflow-x-auto max-w-40"}>
                {variant === "wps" ? (
                  item.name
                ) : (
                  <TooltipParagraph text={item.name} className="whitespace-pre-wrap max-w-[280px]" />
                )}
              </td>
              <td className={variant === "wps" ? "td min-w-40" : "td overflow-x-auto"}>
                {formatDate(item.start_date)}
              </td>
              <td className="td">
                <SwitchButton
                  checked={item.pickup}
                  disabled={!isHaveActionPermission(config.permissionResource, config.permissionApp, "edit")}
                  onChange={() => toggleActive(item.id, item.pickup)}
                />
              </td>
              <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                <EventActions config={config} item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const PickupEventListPage = ({ variant }: { variant: PickupEventVariant }) => {
  const config = getConfig(variant);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const labels = getEventLabels(variant, translate);
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const { tableQuery, filters, setFilters } = useTable<PickupEventItem>({
    resource: config.listResource,
    syncWithLocation: true,
    pagination: {
      mode: "off",
      pageSize: PAGE_SIZE,
    },
    meta: {
      resource: "data",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) => ("field" in item ? item : []));

    return {
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
    };
  }, [filters]);

  const handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap((item) => ("field" in item ? item : []));
    const pageIndex = updatedFilters.findIndex((filter) => filter.field === "page");

    if (pageIndex < 0) {
      updatedFilters.push({ field: "page", operator: "eq", value: page });
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  };

  const toggleActive = (id: string, isActive: boolean) => {
    if (!window.confirm(labels.toggleConfirm)) return;

    updateMutation(
      {
        resource: config.updateResource,
        id,
        meta: {
          URLSuffix: isActive ? "hide" : "display",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: (res) => {
          toast.success(getMessage(get(res, "data.message"), language, labels.updateSuccess));
          tableQuery.refetch();
        },
      },
    );
  };

  const data = (tableQuery.data?.data ?? []) as PickupEventItem[];
  const currentPage = tableQuery.data?.paging?.current_page ?? 1;
  const pageSize = tableQuery.data?.pageSize ?? PAGE_SIZE;
  const hasMultiplePages = (tableQuery.data?.paging?.total_page ?? 0) > 1;

  return (
    <div className={variant === "wps" ? "w-full min-w-0 max-w-full overflow-hidden" : "wrapper"}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn, href: config.basePath },
                { label: labels.pageTitle, href: config.basePath },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
      />

      <div className={variant === "wps" ? "w-full p-[16px] flex flex-col gap-[16px]" : "w-full flex flex-col gap-y-4"}>
        {variant === "admin" ? (
          <div className="box-container">
            <SharedFilter
              config={config}
              currentFilterValues={currentFilterValues}
              setFilters={setFilters}
              variant={variant}
            />
          </div>
        ) : (
          <SharedFilter
            config={config}
            currentFilterValues={currentFilterValues}
            setFilters={setFilters}
            variant={variant}
          />
        )}

        <div className="box-container">
          {!data.length && !tableQuery.isFetching ? (
            <div className="text-center">{labels.noData}</div>
          ) : null}

          {data.length > 0 ? (
            <>
              <div className="sm:hidden">
                <MobileCards
                  config={config}
                  currentPage={currentPage}
                  data={data}
                  hasPagination={hasMultiplePages}
                  labels={labels}
                  pageSize={pageSize}
                  toggleActive={toggleActive}
                  variant={variant}
                />
              </div>
              <div className="hidden sm:block">
                <DesktopTable
                  config={config}
                  currentPage={currentPage}
                  data={data}
                  hasPagination={hasMultiplePages}
                  labels={labels}
                  pageSize={pageSize}
                  toggleActive={toggleActive}
                  variant={variant}
                />
              </div>
            </>
          ) : null}

          {hasMultiplePages && tableQuery?.data?.paging ? (
            <div className="mt-6">
              <Pagination
                currentPage={tableQuery.data.paging.current_page}
                totalPage={tableQuery.data.paging.total_page}
                total={{
                  totalItems:
                    tableQuery.data.paging?.total_item ??
                    tableQuery.data.paging?.total_items ??
                    0,
                  total:
                    tableQuery.data.paging?.total ??
                    tableQuery.data.paging?.total_qas ??
                    0,
                }}
                onChange={handlePageChanged}
                hideTotal
              />
            </div>
          ) : null}
        </div>
      </div>

      {tableQuery.isLoading ||
      updateMutationIsLoading ||
      (tableQuery.isFetching && tableQuery.isPreviousData) ? (
        <FullScreenLoading />
      ) : null}
    </div>
  );
};
